/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

// library imports
import { Component, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import {
    PayPalButtons, PayPalHostedField, PayPalHostedFieldsProvider,
    PayPalScriptProvider, usePayPalHostedFields, usePayPalScriptReducer
} from "@paypal/react-paypal-js";

// project imports
import store from "features/store";
import { MyAccountMenuList, ToggleSideBar, removeSideBar } from "../MyAccountMenuList";
import { PayPalClientId, PAYPAL_SANDBOX, PAYPAL_BASE_URL, PAYPAL_DESCRIPTION } from "shared/settings/config";
import {
    authorizeOrder, createOrder, generateAccessToken, generateClientToken,
    paypalCustomerId
} from "shared/services/PaypalServices";
import { CityAndStateService, PaymentServices } from "shared/services";
import { ArrayValueReorder } from "shared/modifiers";
import { ToasterMessage } from "shared/toast/toast";
// import moment from 'moment';

// style imports
import './PaymentMethodsAdd.scss';
import { DebounceInput } from "react-debounce-input";
import { addSpacesForCardNumber, findCard } from "../../../../../../shared/validators/cardValidation/cardValidation";
import { addSpacesForExpirationDate, expirationDate } from "shared/validators/expiryDateValidation/expirationDate";
import Moment from 'moment';
import { pmAutoNavigateToNextScreen } from "shared/utils/utils";
import { setUser } from "shared/settings/auth";
import { HeaderHOC } from "../headerHOC/HeaderHOC";
import pmservice from "shared/services/pmservice";

/** Paypal additional fields and save payment method integration */
export const AddCard = (props) => {
    // const hostedFields = usePayPalHostedFields();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [unit, setUnit] = useState({value: ''});
    const useCreateState = () => {
        const [control, setControl] = useState({ value: '', isValid: false, isPotentiallyValid: false });
        return { ...control, setControl };
    };
    const [stateCityError, setStateCityError] = useState('');
    const [stateCityIsValid, setStateCityIsValid] = useState(true);
    const [cityDropdownValue, setCityDropdownValue] = useState([]);
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const errorMessage = "The Zip code, State and City not matching please enter correct information";

    const [cardDetails, setCardDetails] = useState({});
    const [cardIsValid, setCardIsValid] = useState(false);
    const [cardErrorMessage, setCardErrorMessage] = useState();
    const [focused, setFocused] = useState(false)
    const [previousProfile, setPreviousProfile] = useState(props.user);
    const [zipcodeError, setZipcodeError] = useState('');

    const cardFields = {
        cardNumber: useCreateState(),
        expiryDate: useCreateState(),
        cvv: useCreateState(),
        cardHolderName: useCreateState(),
        streetAddress: useCreateState(),
        zipCode: useCreateState(),
        state: useCreateState(),
        city: useCreateState()
    };

    useEffect (() => {
        setCardIsValid(cardFields.cardNumber.isValid && cardFields.expiryDate.isValid && cardFields.cvv.isValid && 
            cardFields.cardHolderName.isValid && cardFields.streetAddress.isValid && cardFields.zipCode.isValid && cardFields.state.isValid &&
            cardFields.city.isValid ? true : false);
    }, [cardFields]) 

    const onFocus = () => {
        setFocused(true);
        setCardErrorMessage('');
    }

    const onSubmitCard = () => {
        setSaveLoading(true);
        let expiryDate = cardFields.expiryDate.value;
        let date = Moment(expiryDate, "MMYYYY");
        expiryDate = date.format("YYYY-MM");
        const cardData = {
            data: {
                buyerid: props.user.id,
                cardlastfourdigit: cardFields.cardNumber.value.replace(/ /g, ''),
                customerid: paypalCustomerId(props.user.id),
                securitycode: cardFields.cvv.value,
                cardexpirydate: expiryDate,
                cardbrand: cardDetails?.niceType ? cardDetails.niceType : null,
                cardtype: 'CREDIT',
                name: cardFields.cardHolderName.value,
                billingaddress: {
                    address_line_1: cardFields.streetAddress.value,
                    address_line_2: unit.value,
                    admin_area_1: cardFields.state.value,
                    admin_area_2: cardFields.city.value,
                    postal_code: cardFields.zipCode.value,
                    country_code: 'US'
                }
            }
        };

        if (props?.platform == 'mobile') {
            sendMessageToMobileApp("SUCCESS", cardDetails);
        } else {
            PaymentServices.addVaultCard(cardData).then(res => {
                setSaveLoading(false);
                if(res.data.data.hasOwnProperty("link")){
                    window.open(res.data.data.link, '_self');
                } else {
                    if (props.user.paymentcompleted) {
                        setShowSuccessModal(true);
                    } else {
                        setUser(props.user.id).then(() => {
                            setShowSuccessModal(true);
                        })
                    }
                }
            }).catch((e) => {
                setSaveLoading(false);
                setCardErrorMessage(e.code ? `${e.code} : ${e.message}` : e.message)
            });
        }
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);

        if ( props.location?.state?.name == "bidscreen" ) {
            props.history.push(props.location.state.previouspath);
        } 
        else {
            pmAutoNavigateToNextScreen(props, previousProfile, props.user, "paymentcompleted", '/my-account/my-info/payment-methods');
        }
    }

    const validateZipcode = (data) => {
        let zipValue = data.replace(/[^\d]+/g, '');
        let zipCode = zipValue.substring(0, 5);
        let externalZipCode = zipValue.substring(5);
        let concatenatedZipcode = zipCode + (externalZipCode ? '-' + externalZipCode : '');
        if(zipValue.length > 5 && zipValue.length < 9) {
            zipValue = concatenatedZipcode;
            setZipcodeError("Zipcode should be either 5 or 9 digits");
        } else if(zipValue.length == 5 || zipValue.length == 9) {
            zipValue = concatenatedZipcode;
            setZipcodeError("");
        }
        cardFields.zipCode.setControl({ value: zipValue, isValid: Boolean(zipValue) });
        validateStateAndCity(zipValue, cardFields.state.value, cardFields.city.value);
    }

    const onStateChange = (event) => {
        cardFields.state.setControl({ value: event.target.value, isValid: Boolean(event.target.value) });
        validateStateAndCity(cardFields.zipCode.value, event.target.value, cardFields.city.value);
    };

    const validateCity = (data) => {
        const alpha = /^[A-Za-z\s]*$/;
        if(data.match(alpha))
            cardFields.city.setControl({ value: data, isValid: Boolean(data) });
        validateStateAndCity(cardFields.zipCode.value, cardFields.state.value, data);
        if(cityDropdownValue.length > 0 && !showCityDropdown){
            setShowCityDropdown(true);
        }
    }

    const validateStateAndCity = (zipcode, state, city) => {
        setStateCityIsValid(false)
        const data = {
            "data": {
                "zipcode": zipcode.length > 5 ? zipcode.substring(0,5) : zipcode,
                "state": state,
                "city": city
            }
        };
        if(state != "" && city != "" && zipcode != "") {
            CityAndStateService.checkCityState(data).then(response => {
                setStateCityError('')
                setStateCityIsValid(true);
                setShowCityDropdown(false)
            }).catch(error => {
                if(error.status === 400 || error.status == "failed") {
                    setStateCityError(errorMessage);
                    setStateCityIsValid(false);
                    setCityDropdownValue(error.error.data);
                }
            });
        }
    }

    const sendMessageToMobileApp = (status, data) => {
        status = status.toLowerCase();
        let responsePayload = {
            "status": status
        }
        if (status === 'success') {
            responsePayload['data'] = data;
        }
        window?.ReactNativeWebView?.postMessage(JSON.stringify(responsePayload));
    }

    const setCityValue = (city) => {
        cardFields.city.setControl({ value: city, isValid: Boolean(city) });
        setShowCityDropdown(false);
        validateStateAndCity(cardFields.zipCode.value, cardFields.state.value, city );
    }

    const handleCardValidations = (type, data) => {
        let value = data;
        switch (type) {
            case "card":
                let cardValidation = findCard(value);
                let cardValue = addSpacesForCardNumber(cardFields.cardNumber.value, value, cardValidation?.card?.gaps);
                cardFields.cardNumber.setControl({ value: cardValue, isValid: cardValidation.isValid, isPotentiallyValid: cardValidation.isPotentiallyValid });
                cardFields.cvv.setControl((prevState) => { return { ...prevState, isValid: cardValidation?.card?.code?.size ? cardValidation.card.code.size == prevState.value.length : false } });
                setCardDetails(cardValidation?.card ? cardValidation.card : {});
                break;
            case "expiry":
                let dateValidation = expirationDate(value);
                let dateValue = addSpacesForExpirationDate(cardFields.expiryDate.value, value);
                cardFields.expiryDate.setControl({ value: dateValue, isValid: dateValidation.isValid, isPotentiallyValid: dateValidation.isPotentiallyValid }); 
                break;
            case "cvv":
                cardFields.cvv.setControl({ value: value, isValid: cardDetails?.code?.size ? cardDetails.code.size == value.length : false });
                break;
            default:
                break;
        }
    };

    const blockSpaceBar = (event) => {
        let backspace = 32;
        if (event.keyCode == backspace) event.preventDefault();
    }  

    return <>
        <div>
            <label className="m-0"><b>Card Number<sup className="text-danger">*</sup></b></label>
            <input type="text" onFocus={onFocus} onKeyDown={(e) => {return blockSpaceBar(e)}} maxLength={cardFields.cardNumber.isValid ? cardFields.cardNumber.value.length : 20} style={{ outline: "none", color: cardFields.cardNumber.isValid ? 'rgb(40, 167, 69)' : !cardFields.cardNumber.isValid && !cardFields.cardNumber.isPotentiallyValid ? 'rgb(220, 53, 69)' : '' }} id="card-number" name="card-number" className="card-field"
                placeholder='XXXX-XXXX-XXXX-XXXX' value={cardFields.cardNumber.value} onChange={(event) => { handleCardValidations("card", event.target.value) }} />
        </div>

        <div className="d-flex flex-column flex-sm-row justify-content-between" style={{ gap: '10%' }}>
            <div>
                <label className="m-0"><b>Expiration Date<sup className="text-danger">*</sup></b></label>
                <input type="text" onFocus={onFocus} onKeyDown={(e) => {return blockSpaceBar(e)}} style={{ outline: "none", color: cardFields.expiryDate.isValid ? 'rgb(40, 167, 69)' : !cardFields.expiryDate.isValid && !cardFields.expiryDate.isPotentiallyValid ? 'rgb(220, 53, 69)' : '' }} id="expiration-date" name="expiration-date" className="card-field"
                    placeholder="MM/YYYY" value={cardFields.expiryDate.value} onChange={(event) => { handleCardValidations("expiry", event.target.value) }} />
            </div>

            <div>
                <label className="m-0"><b>CVV/CVC<sup className="text-danger">*</sup></b></label>
                <input type="password" onFocus={onFocus} onKeyDown={(e) => {return blockSpaceBar(e)}} style={{ outline: "none", color: cardFields.cvv.isValid ? 'rgb(40, 167, 69)' : !cardFields.cvv.isValid ? 'rgb(220, 53, 69)' : '' }} id="cvv" name="cvv" className="card-field"
                    placeholder={cardDetails?.code?.size ? cardDetails.code.size == 4 ? '****' : '***' : '***'} value={cardFields.cvv.value} maxLength={cardDetails?.code?.size ? cardDetails.code.size : 3} onChange={(event) => { handleCardValidations("cvv", event.target.value) }} />
            </div>
        </div>

        <label className="m-0"><b>Name on Card<sup className="text-danger">*</sup></b></label>
        <input id="card-holder" className="card-field" style={{ outline: "none" }} 
            type="text" onFocus={onFocus} placeholder="Name on Card" value={cardFields.cardHolderName.value} 
            onChange={(e) => cardFields.cardHolderName.setControl({ value: e.target.value, isValid: Boolean(e.target.value) })} autoComplete="none" />
        <p className="text-danger text-center d-flex justify-content-center">PO boxes cannot be used as your billing address.</p>
        <div className="d-flex justify-content-between mt-4">
            <b>Billing Address<sup className="text-danger">*</sup></b>
            <img src={`${process.env.PUBLIC_URL}/static/icons/flags/flag-us.png`} alt="flag-US" style={{ height: '25px', width: '25px' }} />
        </div>

        <input id="street-address" className="card-field" style={{ outline: "none" }} 
            type="text" onFocus={onFocus} placeholder="Street Address" value={cardFields.streetAddress.value} 
            onChange={(e) => cardFields.streetAddress.setControl({ value: e.target.value, isValid: Boolean(e.target.value) })} autoComplete="none" />

        <input id="unit" className="card-field" style={{ outline: "none" }} 
            type="text" onFocus={onFocus} placeholder="Unit" value={unit.value} 
            onChange={(e) => setUnit({value: e.target.value})} autoComplete="none" />

        <input id="zip-code" className={`card-field ${zipcodeError ? 'mb-zip' : ''}`} style={{ outline: "none" }} 
            type="text" onFocus={onFocus} placeholder="ZIP / Postal Code" value={cardFields.zipCode.value} 
            onChange={(e) => validateZipcode(e.target.value)} minLength="5" maxlength="10" autoComplete="none" />

        {zipcodeError ? <small className="text-danger">{zipcodeError}</small> : ''}

        <select id="state" className="card-field" aria-label="select state" style={{ padding: '0px 12px', outline: "none"}}
            value={cardFields.state.value} onChange={onStateChange} onFocus={onFocus}>
            <option className="d-none" value="">Select State</option>
            {
                props.stateList.map((state, index) => <option key={`state-${index}`} value={state.state}>{state.state}</option>)
            }
        </select>

        <DebounceInput id="city" className={`card-field ${stateCityError ? 'card-field-error': ''}`} style={{ outline: "none" }} 
            type="text" placeholder="City" value={cardFields.city.value} 
            onChange={(e) => validateCity(e.target.value)} onFocus={onFocus} autoComplete="none" debounceTimeout={200} /> 
            {showCityDropdown ?
                <div className='card-city-value'>
                    {cityDropdownValue.map((elem, index) => (
                        <div className='pd-15' id={index} onClick={() => { setCityValue(elem.city) }}>{elem.city}</div>
                    ))}
                </div> : ""}

        <div>
            {stateCityError ? <small className="text-danger">{stateCityError}</small> : ''}
        </div>

        <input id="termsAccepted" onChange={() => setTermsAccepted(!termsAccepted)} value={termsAccepted} type="checkbox" style={{ outline: null, border: '1px solid #ccc', cursor: 'pointer' }} />
        <label htmlFor="termsAccepted" className="clickable">&ensp;Click here to accept our&nbsp;</label>
        <u className="text-info clickable" onClick={() => {window.open("https://assets.ctbids.com/buyer/CTBidsTermsandConditions1.10.25.pdf", "_blank")}}>Terms and Conditions</u>
        
        <div>
            <p>Your Payment details will be stored in the account and will be charged upon winning an item.  An initial $1.01 charge will be reflected on your card to verify the payment method and will refund shortly after.</p>
        </div>
        <div className="d-flex flex-column flex-sm-row justify-content-between pbt-6" style={{ position: 'relative', gap: '10%'}}>
            <Button  variant="" disabled={!termsAccepted || saveLoading || !stateCityIsValid || !cardIsValid } className="my-2 w-100 save-btn" onClick={onSubmitCard}>Save {saveLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : ''}</Button>
            <Button className="my-2 w-100" onClick={() => props.platform === 'mobile' ? sendMessageToMobileApp("CANCEL") : props.history.push(props.location?.state?.name == "bidscreen" ? props.location.state.previouspath :'/my-account/my-info/payment-methods')} disabled={ saveLoading }>Cancel</Button>
            <div style={{ position: 'absolute', bottom: "-5px", color: "#ff2020", fontSize: '14px' }}>
                {cardErrorMessage}
            </div>
        </div>
        <Modal animation={false} centered show={showSuccessModal} backdrop="static" keyboard={false}>
            <Modal.Body>
                <div className="body-content">
                    <p style={{textAlign: "center"}}>Your account is now complete! You may now place a bid. Happy Bidding!</p>
                </div>
                <div className="d-flex align-items-center justify-content-center actions" style={{marginTop: "2rem"}}>
                    <Button variant="secondary mx-2" onClick={()=> {closeSuccessModal()}}>Close</Button>
                </div>
            </Modal.Body>
        </Modal>
    </>
};

const ButtonWrapper = (props) => {

    const [{ isPending }, dispatch] = usePayPalScriptReducer();

    useEffect(() => {
        props.onPaypalLoadingCallback(isPending);
    });

    const onApprovePayPal = async (data) => {
        let payloadData = {
            authorizeorderid: data.orderID
        }
        props.onApprovalCallBack(null)
        PaymentServices.vaultPayPalAccount({"data" : payloadData}).then(res => {
            if (!props.user.paymentcompleted) {
                store.dispatch({ type: 'SET_USER', payload: { ...props.user, paymentcompleted: 1 }});
            }
            props.onApprovalCallBack(res.data)
            return data.orderID
        }).catch((e) => {
            props.onApprovalCallBack(e)
        });
    }

    return <>
        <PayPalButtons fundingSource="paypal"
                createOrder={async (data, actions) => {
                    const response = await fetch(`${PAYPAL_BASE_URL}/v2/checkout/orders`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${await generateAccessToken()}`,
                            "PayPal-Request-Id": new Array(23).join().replace(/(.|$)/g, function () { return ((Math.random() * 36) | 0).toString(36); })
                        },
                        body: JSON.stringify({
                            "intent": "AUTHORIZE",
                            "purchase_units": [{
                                "description": PAYPAL_DESCRIPTION,
                                "amount": {
                                    "currency_code": "USD",
                                    "value": "1.01"
                                }
                            }],
                            "payment_source": {
                                "paypal": {
                                    "attributes": {
                                        "vault": {
                                            "store_in_vault": "ON_SUCCESS",
                                            "usage_type": "MERCHANT",
                                            "customer_type": "CONSUMER"
                                        }
                                    },
                                    "experience_context": {
                                        "return_url": "https://ctbids.com/returnUrl",
                                        "cancel_url": "https://ctbids.com/cancelUrl"
                                    }
                                }
                            }
                        })
                    });
                    let res = await response.json();
                    return res.id;
                }}
                onApprove={ (data, actions) => { onApprovePayPal(data) }}
            />
    </>
}

const AddPayPalAccount = (props) => {

    return <>
        <PayPalScriptProvider options={props.options}>
            <ButtonWrapper {...props} />
        </PayPalScriptProvider>
    </>
}


/* Form to vault card */
class PaymentMethodsAdd extends Component {

    constructor(props) {
        super(props);
        this.previousProfile = props.user;

        this.state = {
            pageLoading: false,
            termsModalShow: false,
            stateList: [],
            showCredit: false,
            showPayPal: true,
            showPayPalSuccessModal: false,
            showPayPalErrorModal: false,
            payPalErrorMessage: '',
            buttonLoading: true,
            showPayPalKeyWarningModal: false,
            paypalMessage: {'status': '', 'message': ''},
            paypalProcessBack: false,
            paypalCancel: false,
            showPaypalPage: this.props?.match?.params?.paypalkey ? {'show': true} : this.props.match.path == '/vault/cancel' ? {'show': false} : {},
            paymentMethodMessage: ''
        };

        this.paypalKey = this.props?.match?.params?.paypalkey;

        this.paypalAccountSdkOptions = {
            'client-id': PayPalClientId,
            'data-client-token': '',
            components: 'buttons',
            intent: 'authorize',
            vault: true
        };
    }

    async componentDidMount() {
        store.dispatch({ type: 'SIDEBAR_MENU_LIST', payload: { menuList: MyAccountMenuList(), withSpan: false } });
        this.windowResizeHandle();
        window.addEventListener('resize', this.windowResizeHandle);
        
        if(!this.paypalKey && Object.keys(this.state.showPaypalPage).length === 0) {
            this.setState({
                pageLoading: true
            })
            await CityAndStateService.getStateList().then(res => {
                this.setState({ stateList: ArrayValueReorder(res.data.data, 'state'), pageLoading: false });
            }).catch( () => this.setState({ pageLoading: false }));
        } else if (!this.paypalKey && !this.state.showPaypalPage.show) {
            this.setState({
                paypalMessage: {
                    'status': 'failed',
                    'message': 'The Payment method has failed , due to some reasons'
                },
                paypalCancel: true
            })
        } else {
            this.setState({
                paypalMessage: {
                    'status': 'processing',
                    'message': 'Please wait while we are processing your Payment Method'
                },
                paypalProcessBack: true
            })
            let data = {
                id: this.paypalKey
            }
            PaymentServices.vaultCardVerify({"data" : data}).then(res => {
                if(res.data.status == 'success') {
                    setUser(this.props.user.id).then(() => {
                        this.setState({
                            showPayPalSuccessModal: true,
                            paypalProcessBack: false
                        })
                    })  
                }
            }).catch((e) => {
                this.setState({
                    paypalMessage: {
                        'status': 'failed',
                        'message': 'The Payment method has failed , Processing of this card type is not supported. Use another type of card'
                    },
                    paypalProcessBack: false
                })
            });
        }
        this.getPaymentContentMessage();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.windowResizeHandle);
        removeSideBar();
    }

    windowResizeHandle = () => {
        ToggleSideBar();
    };

    handleCredit = () => {
        this.setState({
            showPayPal: false,
            showCredit: true
        })
    }

    closePaypalSuccessModal = () => {
        this.setState({
            showPayPalSuccessModal: false
        });

        if ( this.props.location?.state?.name == "bidscreen" ) {
            this.props.history.push(this.props.location.state.previouspath);
        } else {
            pmAutoNavigateToNextScreen(this.props, this.previousProfile, this.props.user, "paymentcompleted", '/my-account/my-info/payment-methods');
        }
    }

    closeErrorModal = () => {
        this.setState({
            showPayPalErrorModal: false
        })
    }

    navigatetoBack = () => {
        if (this.props.location?.state?.name == "bidscreen") {
            this.props.history.push(this.props.location.state.previouspath);
        } else if (this.paypalKey && this.state.paypalProcessBack) {
            this.setState({
                showPayPalKeyWarningModal: true
            })
        } else {
            this.setState({
                paypalCancel: false
            })
            this.props.history.push("/my-account/my-info/payment-methods");
        }
    }

    closePaypalKeyErrorModal = () => {
        this.setState({
            showPayPalKeyWarningModal: false,
            paypalProcessBack: false
        })
    }

    getPaymentContentMessage = () => {
        pmservice.getContent('paymentmethod', '').then((data) => {
            let message = JSON.parse(data.data.data[0].jsoncontent);
            this.setState({
                paymentMethodMessage: message.text
            })
          })
          .catch((error) => {
            this.setState({
                paymentMethodMessage: ''
            })
          });
    }

    render() {
        return (
            <div className="payment-method-add p-4 top-border">
                <b style={{ backgroundColor: '#A50050', border: '1px solid #A50050', color: 'white' }} className="clickable p-2" onClick={()=> {this.navigatetoBack()}}><i className="fas fa-chevron-left" /> Back</b>
                {/* <HeaderHOC {...this.props}/> */}
                <div className="d-flex align-items-center justify-content-center">
                    <h4 className="text-secondary">Add a New Payment Method</h4>
                </div>
                {!this.paypalKey && !this.state.paypalCancel ? <div className="payment-card"  style={{maxWidth: '400px'}}>
                    <div>WE ACCEPT</div>
                    <div>
                        <img src={`${process.env.PUBLIC_URL}/static/images/Card/Visa.svg`} className="payment-card-icon" alt="visa-card-icon"></img>
                        <img src={`${process.env.PUBLIC_URL}/static/images/Card/Master_card.svg`} className="payment-card-icon" alt="master-card-icon"></img>
                        <img src={`${process.env.PUBLIC_URL}/static/images/Card/American_Express.svg`} className="payment-amer-card-icon" alt="amex-card-icon"></img>
                        <img src={`${process.env.PUBLIC_URL}/static/images/Card/Discover.svg`} className="payment-card-icon" alt="discover-card-icon"></img>
                        <img src={`${process.env.PUBLIC_URL}/static/images/Card/PayPal.png`} className="paypal-card-icon" alt="paypal-card-icon"></img>
                    </div>
                </div> : ''}
               
                {
                    this.state.pageLoading ?
                        <div className="loader d-flex align-items-center justify-content-center">
                            <img src={`${process.env.PUBLIC_URL}/static/icons/table/loader.gif`} className="loader-icon" alt="loader-icon"></img>
                        </div> 
                        :
                        this.paypalKey ? 
                            <div className="d-flex justify-content-center mt-3 flex-column align-items-center f-20" style={{ color: this.state.paypalMessage.status == 'failed' ? '#ff2020' : this.state.paypalMessage.status == 'success' ? '#497637' : '' }}>
                                {this.state.paypalMessage.message}
                            </div> : this.state.paypalCancel ? 
                            <div className="d-flex justify-content-center mt-3 flex-column align-items-center f-20" style={{ color: this.state.paypalMessage.status == 'failed' ? '#ff2020' : this.state.paypalMessage.status == 'success' ? '#497637' : '' }}>
                                {this.state.paypalMessage.message}
                            </div> : 
                            <div className="d-flex justify-content-center mt-3 flex-column align-items-center">

                                {this.state.showPayPal && !this.paypalKey ? <div style={{ width: "362px" }}>
                                    <AddPayPalAccount {...this.props} options={this.paypalAccountSdkOptions} onApprovalCallBack={
                                        (data) => {
                                            this.setState({
                                                pageLoading: data?.status == 'success' ? false : (data?.status == 400 || data?.status == 401) ? false : true,
                                                showPayPalSuccessModal: data?.status == 'success' ? true : false,
                                                showPayPalErrorModal: (data?.status == 400 || data?.status == 401) ? true : false,
                                                payPalErrorMessage: data?.message
                                            })
                                        }} onPaypalLoadingCallback={(isLoading) => {
                                            if (!isLoading) {
                                                this.setState({
                                                    buttonLoading: isLoading
                                                });
                                            }
                                        }} />
                                </div> : ""}

                                {!this.state.showCredit && !this.state.buttonLoading ?
                                    <div style={{ marginTop: "10px" }}>
                                        <Button onClick={this.handleCredit} className="card-btn d-flex align-items-center justify-content-center"><div style={{ padding: "7px" }}><img className="credit-card-icon" src={`${process.env.PUBLIC_URL}/static/images/Card/Credit_card.png`} alt="card-icon" /></div>Credit Card or Debit Card</Button>
                                    </div> : ""}

                                {this.state.showCredit ? <div className="rounded p-3" style={{ backgroundColor: '#f6f6f6', maxWidth: '400px', zIndex: '9' }}>
                                    <AddCard {...this.props} termsModalOpen={() => this.setState({ termsModalShow: true })}
                                        stateList={this.state.stateList} />
                                </div> : ""}

                                {!this.state.showCredit && this.state.paymentMethodMessage ? <div className="mt-4 text-secondary" style={{ fontSize: "1.3125rem" }}>{this.state.paymentMethodMessage}</div> : ''}

                            </div>
                }
                <Modal animation={false} centered show={this.state.showPayPalSuccessModal} backdrop="static" keyboard={false}>
                    <Modal.Body>
                        <div className="body-content">
                            <p style={{ textAlign: "center" }}>Your account is now complete! You may now place a bid. Happy Bidding!</p>
                        </div>
                        <div className="d-flex align-items-center justify-content-center actions" style={{ marginTop: "2rem" }}>
                            <Button variant="secondary mx-2" onClick={() => { this.closePaypalSuccessModal() }}>Close</Button>
                        </div>
                    </Modal.Body>
                </Modal>

                <Modal animation={false} centered show={this.state.showPayPalErrorModal} backdrop="static" keyboard={false}>
                    <Modal.Body>
                        <div className="body-content">
                            <div className="error-bg">
                                <p>{this.state.payPalErrorMessage}</p>
                            </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-center actions" style={{ marginTop: "2rem" }}>
                            <Button variant="secondary mx-2" onClick={() => { this.closeErrorModal() }}>Close</Button>
                        </div>
                    </Modal.Body>
                </Modal>

                <Modal animation={false} centered show={this.state.showPayPalKeyWarningModal && this.state.paypalProcessBack} backdrop="static" keyboard={false}>
                    <Modal.Body>
                        <div className="body-content">
                            <div className="error-bg">
                                <p>Are you sure you want to exit this screen, You payment method is processing currently and moving away from this screen will may fail the Payment method addition</p>
                            </div>
                        </div>
                        <div className="d-flex align-items-center justify-content-center actions" style={{ marginTop: "2rem" }}>
                            <Button variant="secondary mx-2" onClick={() => { this.closePaypalKeyErrorModal() }}>Close</Button>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        );
    }
}

export default connect(state => state)(PaymentMethodsAdd);